import { useEffect, useState } from 'react'
import { GetRunsParams, getRuns } from 'utils/api'
import { useQueryClient } from 'react-query'
import { Box, IconButton, Skeleton, Stack, styled, Typography } from '@mui/material'
import AgGridRunDetailsTable from 'components/shared/AgGridRunDetailsTable'
import useCellImage from 'utils/useCellImage'
import useCellImageLayerUrl from 'utils/useCellImageLayerUrl'
import PlusIcon from 'components/shared/icons/PlusIcon'
import MinusIcon from 'components/shared/icons/MinusIcon'
import { AgGridReactProps } from 'ag-grid-react'
import useCellBrowsingSlice from 'redux/slices/hooks/useCellBrowsingSlice'
import { getCellDetailsColumnDefs, getCellDetailsRowData } from './tableView/metadata'
import { SearchOptions } from './types'
import useCellBrowsingQueryParams from './useCellBrowsingQueryParams'

// Stored crops are 256px; mirror the masked image's center-crop zoom onto the raw crop.
const RAW_BLOB_SIZE = 256

interface Props extends AgGridReactProps {
  searchOptions: SearchOptions
}

const ZoomIconContainer = styled(Box)({
  display: 'flex',
  gap: '6px',
  position: 'absolute',
  bottom: '10px',
  left: '10px',
})

function CellDetailsPanel(props: Props): JSX.Element {
  const [cellDescription, setCellDescription] = useState<string | undefined>('')

  const { searchOptions, onGridReady } = props

  const queryClient = useQueryClient()

  const {
    cellBrowsing: { selectedCell },
  } = useCellBrowsingSlice()

  useEffect(() => {
    if (searchOptions?.runId && searchOptions.runId.trim() !== '') {
      const params = { run_ids: [searchOptions.runId] } as GetRunsParams
      ;(async () => {
        try {
          const responseRunCellData = await queryClient.fetchQuery(
            ['getCellRunData', params],
            getRuns,
            {
              staleTime: 0,
            }
          )
          setCellDescription(responseRunCellData[0].description)
        } catch (err) {
          console.error(
            `Unable to fetch the description of Run Id ${searchOptions.runId} associated with the cell image`
          )
        }
      })()
    }
  }, [queryClient, searchOptions.runId])

  const IMAGE_SIZE = 250
  const [zoomImage, setZoomImage] = useState(IMAGE_SIZE)

  const { data: imageDataUrl } = useCellImage({
    cellId: selectedCell.cell.getCellId(),
    frame: 0,
    width: 250,
    filters: { centerCropSize: zoomImage },
  })

  // Raw-compare mode: also show the raw (unmasked) crop for the same cell.
  const { displayOptions } = useCellBrowsingQueryParams()
  const showRawCrop = Boolean(displayOptions?.showRawCrop)
  const { data: rawCropUrl } = useCellImageLayerUrl(
    selectedCell.cell.getCellId(),
    'raw_crop',
    0,
    showRawCrop
  )

  // Reset Cell image size when cellId changes
  useEffect(() => {
    setZoomImage(IMAGE_SIZE)
  }, [selectedCell])

  const handleZoomIn = () => {
    setZoomImage((prev) => {
      if (prev > 150) {
        return 150
      }
      return prev
    })
  }

  const handleZoomOut = () => {
    setZoomImage((prev) => {
      if (prev < IMAGE_SIZE) {
        return IMAGE_SIZE
      }
      return prev
    })
  }

  if (!selectedCell?.cell.getCellId()) {
    return <></>
  }

  const rowData = getCellDetailsRowData(
    selectedCell.cell,
    selectedCell.cell.getCellId(),
    cellDescription
  )

  return (
    <Box>
      <Stack sx={{ my: '10px' }} direction="row" justifyContent="center" gap="10px">
        <Stack alignItems="center" gap="4px">
          {imageDataUrl ? (
            <Box
              sx={{
                width: `${IMAGE_SIZE}px`,
                height: `${IMAGE_SIZE}px`,
                position: 'relative',
              }}
            >
              <img width={IMAGE_SIZE} height={IMAGE_SIZE} src={imageDataUrl} alt="cell" />
              <ZoomIconContainer>
                <IconButton sx={{ p: 0 }} onClick={handleZoomIn}>
                  <PlusIcon inheritViewBox sx={{ width: '23px', height: '23px' }} />
                </IconButton>
                <IconButton sx={{ p: 0 }} onClick={handleZoomOut}>
                  <MinusIcon inheritViewBox sx={{ width: '23px', height: '23px' }} />
                </IconButton>
              </ZoomIconContainer>
            </Box>
          ) : (
            <Skeleton
              variant="rectangular"
              width={IMAGE_SIZE}
              height={IMAGE_SIZE}
              sx={{ borderRadius: '3.5px' }}
            />
          )}
          {showRawCrop ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Masked
            </Typography>
          ) : null}
        </Stack>
        {showRawCrop ? (
          <Stack alignItems="center" gap="4px">
            <Box sx={{ width: `${IMAGE_SIZE}px`, height: `${IMAGE_SIZE}px`, overflow: 'hidden' }}>
              {rawCropUrl ? (
                <img
                  width={IMAGE_SIZE}
                  height={IMAGE_SIZE}
                  src={rawCropUrl}
                  alt="raw crop"
                  style={{
                    transform:
                      zoomImage < RAW_BLOB_SIZE ? `scale(${RAW_BLOB_SIZE / zoomImage})` : undefined,
                  }}
                />
              ) : (
                <Skeleton
                  variant="rectangular"
                  width={IMAGE_SIZE}
                  height={IMAGE_SIZE}
                  sx={{ borderRadius: '3.5px' }}
                />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Raw (unsharpened)
            </Typography>
          </Stack>
        ) : null}
      </Stack>
      <Box>
        <AgGridRunDetailsTable
          testId="cell-details"
          customClass="ag-borderless-header context-menu-ag-grid"
          headerHeight={0}
          columnDefs={getCellDetailsColumnDefs}
          rowData={rowData}
          onGridReady={onGridReady}
        />
      </Box>
    </Box>
  )
}

export default CellDetailsPanel
